/**
 * Provider Profile Validation Hook
 *
 * Provides synchronous validation functions for the provider profile workflow.
 * Follows the same pattern as usePatientValidation for consistency.
 *
 * @module hooks/useProviderValidation
 */

import { useCallback } from 'react';
import type { ValidationResult } from '../types/registration';
import { VALID_PROVIDER_ROLES, type ProviderRole } from '../constants/provider';
import { validatePhoneNumber as validatePhoneUtil } from '../utils/validation/phoneValidation';
import { validateProviderNumber as validateProviderNumberUtil } from '../utils/validation/providerNumberValidation';
import { validateEmail as validateEmailUtil } from '../utils/validation/emailValidation';
import { validatePassword as validatePasswordUtil } from '../utils/validation/passwordValidation';

export interface UseProviderValidationReturn {
  validateEmail: (email: string) => ValidationResult;
  validatePassword: (password: string) => ValidationResult;
  validateName: (name: string, fieldName: string) => ValidationResult;
  validateRole: (role: string) => ValidationResult;
  validateProviderNumber: (providerNumber: string, country?: string) => ValidationResult;
  validatePracticeName: (practiceName: string) => ValidationResult;
  validatePracticeAddress: (practiceAddress: string) => ValidationResult;
  validateBio: (bio: string) => ValidationResult;
  validatePhone: (phoneData: { number: string; countryCode: string; isoCode: string }) => ValidationResult;
  validatePhoto: (file: File) => ValidationResult;
}

export function useProviderValidation(): UseProviderValidationReturn {
  // Email validation
  const validateEmail = useCallback((email: string): ValidationResult => {
    return validateEmailUtil(email);
  }, []);

  // Password validation
  const validatePassword = useCallback((password: string): ValidationResult => {
    return validatePasswordUtil(password);
  }, []);

  // Name validation - using same logic as useValidation but with custom field name
  const validateName = useCallback((name: string, fieldName: string): ValidationResult => {
    if (!name || name.trim().length === 0) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    if (name.trim().length < 1 || name.trim().length > 100) {
      return { isValid: false, error: `${fieldName} must be between 1 and 100 characters` };
    }
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
    }
    return { isValid: true };
  }, []);

  // Role validation
  const validateRole = useCallback((role: string): ValidationResult => {
    if (!role.trim()) {
      return { isValid: false, error: 'Role is required' };
    }

    if (!VALID_PROVIDER_ROLES.includes(role as ProviderRole)) {
      return { isValid: false, error: 'Please select a valid role' };
    }

    return { isValid: true };
  }, []);

  // Provider number validation - using centralized utility
  const validateProviderNumber = useCallback((providerNumber: string, country = 'AU'): ValidationResult => {
    return validateProviderNumberUtil(providerNumber, country);
  }, []);

  // Practice name validation - using same logic as useValidation
  const validatePracticeName = useCallback((practiceName: string): ValidationResult => {
    if (!practiceName || practiceName.trim().length === 0) {
      return { isValid: false, error: 'Practice name is required' };
    }
    if (practiceName.trim().length < 1 || practiceName.trim().length > 100) {
      return { isValid: false, error: 'Practice name must be between 1 and 100 characters' };
    }
    return { isValid: true };
  }, []);

  // Practice address validation - using same logic as useValidation
  const validatePracticeAddress = useCallback((practiceAddress: string): ValidationResult => {
    if (!practiceAddress || practiceAddress.trim().length === 0) {
      return { isValid: false, error: 'Practice address is required' };
    }
    if (practiceAddress.trim().length < 5 || practiceAddress.trim().length > 200) {
      return { isValid: false, error: 'Practice address must be between 5 and 200 characters' };
    }
    // Basic address validation - should contain some alphanumeric characters
    if (!/^[a-zA-Z0-9\s\-,.'#/]+$/.test(practiceAddress.trim())) {
      return { isValid: false, error: 'Practice address contains invalid characters' };
    }
    return { isValid: true };
  }, []);

  // Bio validation - using same logic as useValidation
  const validateBio = useCallback((bio: string): ValidationResult => {
    // Bio is optional, so empty is valid
    if (!bio || bio.trim().length === 0) {
      return { isValid: true };
    }
    if (bio.trim().length > 500) {
      return { isValid: false, error: 'Bio must be 500 characters or less' };
    }
    return { isValid: true };
  }, []);

  // Phone validation using the centralized validation utility
  const validatePhone = useCallback((phoneData: { number: string; countryCode: string; isoCode: string }): ValidationResult => {
    if (!phoneData.number.trim()) {
      return { isValid: false, error: 'Phone number is required' };
    }

    return validatePhoneUtil({
      countryCode: phoneData.countryCode,
      isoCode: phoneData.isoCode,
      number: phoneData.number,
      formatted: phoneData.number,
      international: `${phoneData.countryCode} ${phoneData.number}`
    });
  }, []);

  // Photo validation
  const validatePhoto = useCallback((file: File): ValidationResult => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    if (file.size > maxSize) {
      return { isValid: false, error: 'Photo must be less than 5MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Photo must be JPEG or PNG format' };
    }

    return { isValid: true };
  }, []);

  return {
    validateEmail,
    validatePassword,
    validateName,
    validateRole,
    validateProviderNumber,
    validatePracticeName,
    validatePracticeAddress,
    validateBio,
    validatePhone,
    validatePhoto
  };
}